import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { isValidIban } from '../../../supabase/functions/_shared/payment-account.ts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type PaymentAccount = {
  id: string;
  label: string;
  iban: string;
  bic: string | null;
  is_default: boolean;
};

interface PaymentAccountsProps {
  sciId: string;
  organizationId: string;
}

function accountFields(form: HTMLFormElement) {
  const fields = new FormData(form);
  return {
    label: String(fields.get('label') || '').trim(),
    iban: String(fields.get('iban') || '').replace(/\s/g, '').toUpperCase(),
    bic: String(fields.get('bic') || '').replace(/\s/g, '').toUpperCase() || null,
  };
}

export function PaymentAccounts({ sciId, organizationId }: PaymentAccountsProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<PaymentAccount | null>(null);
  const [adding, setAdding] = useState(false);
  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['sci-payment-accounts', sciId],
    queryFn: async () => {
      const { data, error } = await supabase.from('sci_payment_accounts')
        .select('id, label, iban, bic, is_default')
        .eq('sci_id', sciId)
        .order('is_default', { ascending: false })
        .order('created_at');
      if (error) throw error;
      return data;
    },
  });

  const save = useMutation({
    mutationFn: async ({ fields, accountId }: { fields: ReturnType<typeof accountFields>; accountId?: string }) => {
      if (!isValidIban(fields.iban)) throw new Error('IBAN invalide');
      const response = accountId
        ? await supabase.from('sci_payment_accounts').update(fields).eq('id', accountId).eq('sci_id', sciId)
        : await supabase.from('sci_payment_accounts').insert({
          ...fields, sci_id: sciId, organization_id: organizationId, is_default: accounts.length === 0,
        });
      if (response.error) throw response.error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sci-payment-accounts', sciId] });
      queryClient.invalidateQueries({ queryKey: ['scis'] });
      queryClient.invalidateQueries({ queryKey: ['leases'] });
      queryClient.invalidateQueries({ queryKey: ['receipt-preview'] });
      setAdding(false);
      setEditing(null);
      toast.success('Compte bancaire enregistré');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: async (accountId: string) => {
      const { error } = await supabase.from('sci_payment_accounts')
        .delete().eq('id', accountId).eq('sci_id', sciId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sci-payment-accounts', sciId] });
      toast.success('Compte bancaire supprimé');
    },
    onError: (error: { code?: string; message: string }) => toast.error(error.code === '23503'
      ? 'Compte utilisé par un contrat : réaffectez ce contrat avant de supprimer le compte.'
      : error.message),
  });

  const activeForm = adding || editing;
  return (
    <div className="space-y-3">
      {accounts.map((account) => (
        <div key={account.id} className="flex items-start justify-between gap-2 rounded-md border p-3">
          <div className="min-w-0">
            <p className="font-medium">{account.label}{account.is_default && ' · Principal'}</p>
            <p className="text-sm text-muted-foreground font-mono">IBAN ···· {account.iban.slice(-4)}{account.bic && ` · BIC ${account.bic}`}</p>
          </div>
          <div className="flex shrink-0 gap-1">
            <Button type="button" variant="ghost" size="icon" aria-label={`Modifier ${account.label}`}
              onClick={() => { setAdding(false); setEditing(account); }}><Pencil className="h-4 w-4" /></Button>
            {!account.is_default && <Button type="button" variant="ghost" size="icon"
              aria-label={`Supprimer ${account.label}`} disabled={remove.isPending}
              onClick={() => remove.mutate(account.id)}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        </div>
      ))}
      {!activeForm && <Button type="button" variant="outline" size="sm" disabled={isLoading} onClick={() => setAdding(true)}>
        <Plus className="mr-2 h-4 w-4" /> Ajouter un compte
      </Button>}
      {activeForm && <form key={editing?.id || 'new'} className="space-y-3 rounded-md border p-3" onSubmit={(event) => {
        event.preventDefault();
        save.mutate({ fields: accountFields(event.currentTarget), accountId: editing?.id });
      }}>
        <div><Label htmlFor="payment-account-label">Nom du compte</Label>
          <Input id="payment-account-label" name="label" required defaultValue={editing?.label || ''} placeholder="Compte loyers" /></div>
        <div><Label htmlFor="payment-account-iban">IBAN</Label>
          <Input id="payment-account-iban" name="iban" required defaultValue={editing?.iban || ''} autoComplete="off" /></div>
        <div><Label htmlFor="payment-account-bic">BIC</Label>
          <Input id="payment-account-bic" name="bic" defaultValue={editing?.bic || ''} autoComplete="off" /></div>
        <div className="flex gap-2">
          <Button type="submit" size="sm" disabled={save.isPending}>Enregistrer</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => { setAdding(false); setEditing(null); }}>Annuler</Button>
        </div>
      </form>}
    </div>
  );
}
